import type { Result } from 'true-myth';

import type { HttpFetcherWithBaseUrl } from '~/shared/http-fetcher';
import type { HttpFetcherError } from '~/shared/http-fetcher/http-fetcher';
import type { ValidatingHttpFetcherWithBaseUrl } from '~/shared/validating-http-fetcher';

import type {
    LevelMappingApprovalResponse,
    LevelMappingBucket,
    LevelMappingRun,
    LevelMappingStartRequest,
    LevelMappingUnitChange,
    LevelMappingUnitPage,
} from '../../data-contract/position-level-mapping';
import {
    levelMappingApprovalResponseSchema,
    levelMappingRunSchema,
    levelMappingUnitPageSchema,
} from '../../data-contract/position-level-mapping';
import type {
    HolderInvitationSelection,
    HolderInvitationsPending,
    HolderInvitationsSent,
    InvitationDeliverySummary,
    ManagerChangePreview,
    ManagerChangeRequest,
    ManagerChangeRun,
    ManagerChangeSkippedPositions,
    ManagerChangeStartRequest,
    NodeManagers,
    OrgChartLevel,
    OrgChartRequest,
    PositionAccessArea,
    PositionAccessCandidates,
    PositionAccessCoverage,
    PositionAccessGrant,
    PositionAccessGrantCreateRequest,
    PositionAccessGrantList,
    PositionArchitecture,
    PositionAttachmentList,
    PositionAttachmentPresignFile,
    PositionAttachmentPresignResponse,
    PositionAttribute,
    PositionAttributeCreateRequest,
    PositionAttributeUpdateRequest,
    PositionAttributeValue,
    PositionAttributeValueCreateRequest,
    PositionAttributeValueUpdateRequest,
    PositionBootstrap,
    PositionChartView,
    PositionChartViewRequest,
    PositionCompanyInformation,
    PositionCompanyInformationUpdate,
    PositionCompanyLookup,
    PositionCountsPathEntry,
    PositionCountsRequest,
    PositionCountsResponse,
    PositionCreateRequest,
    PositionDetail,
    PositionEventPage,
    PositionFilterRequest,
    PositionInvitationRequest,
    PositionLevelCounts,
    PositionLevelPickerChildren,
    PositionLevelPickerLevel,
    PositionOperationsPatch,
    PositionPage,
    PositionPerson,
    PositionPlan,
    PositionPlanListItem,
    PositionPlanStartRequest,
    PositionReviewer,
    PositionSaveRequest,
    PositionTaskEditRequest,
    PositionTaskRef,
    PositionWorkQueueCounts,
    PositionWorkspace,
    ReleaseDeferredInvitations,
} from '../../data-contract/position-management';
import {
    holderInvitationsPendingSchema,
    holderInvitationsSentSchema,
    invitationDeliverySummarySchema,
    latestManagerChangeSchema,
    managerChangePreviewSchema,
    managerChangeRunSchema,
    managerChangeSkippedPositionsSchema,
    nodeManagersSchema,
    orgChartLevelSchema,
    positionAccessCandidatesSchema,
    positionAccessCoverageSchema,
    positionAccessGrantListSchema,
    positionAccessGrantSchema,
    positionArchitectureSchema,
    positionAssigneeCandidatesSchema,
    positionAttachmentListSchema,
    positionAttachmentPresignResponseSchema,
    positionAttributeSchema,
    positionAttributeValueSchema,
    positionBootstrapSchema,
    positionChartViewSchema,
    positionCompanyInformationSchema,
    positionCompanyLookupSchema,
    positionCountsResponseSchema,
    positionDetailSchema,
    positionEventPageSchema,
    positionLevelCountsSchema,
    positionLevelPickerChildrenSchema,
    positionLevelPickerLevelsSchema,
    positionPageSchema,
    positionPlanListSchema,
    positionPlanSchema,
    positionReviewerCandidatesSchema,
    positionReviewerSchema,
    positionTaskRefSchema,
    positionWorkQueueCountsSchema,
    positionWorkspaceSchema,
    releaseDeferredInvitationsSchema,
} from '../../data-contract/position-management';

type RequestContext = {
    readonly signal?: AbortSignal;
};

type Fetched<Value> = Promise<Result<Value, HttpFetcherError>>;

export type PositionArchitectureTarget = {
    readonly architectureId: string;
};

export type PositionTarget = {
    readonly positionId: string;
};

export type PositionVersionTarget = PositionTarget & {
    readonly version: number;
};

export type FetchPositionInput = RequestContext & PositionTarget & { readonly version?: number };

export type FetchPositionEventsInput = RequestContext &
    PositionTarget & {
        readonly page: number;
        readonly size: number;
    };

export type FilterPositionsInput = RequestContext &
    PositionArchitectureTarget & {
        readonly request: PositionFilterRequest;
    };

export type CountPositionsInput = RequestContext &
    PositionArchitectureTarget & {
        readonly request: PositionCountsRequest;
    };

export type SearchPositionReviewersInput = RequestContext & {
    readonly query: string;
};

export type SearchPositionAssigneesInput = RequestContext & {
    readonly limit: number;
    readonly query: string;
};

export type PositionLevelPickerTarget = {
    readonly jobArchitectureId: string;
};

export type FetchPositionLevelPickerChildrenInput = RequestContext &
    PositionLevelPickerTarget & {
        readonly parentId: string | null;
    };

export type SearchPositionLevelPickerLevelsInput = RequestContext &
    PositionLevelPickerTarget & {
        readonly languageTag: string;
        readonly limit: number;
        readonly query: string;
    };

export type CreatePositionInput = { readonly request: PositionCreateRequest };

export type CreatePositionArchitectureInput = { readonly jobArchitectureId?: string; readonly name: string };

export type SavePositionInput = PositionTarget & { readonly request: PositionSaveRequest };

export type UpdatePositionOperationsInput = PositionTarget & { readonly operations: PositionOperationsPatch };

export type MapPositionLevelInput = PositionTarget & { readonly mappedLevelItemId: string | null };

export type ArchivePositionInput = PositionTarget & { readonly archived: boolean };

export type AssignPositionReviewerInput = PositionTarget & { readonly userId: string };

export type CreatePositionAttributeInput = { readonly request: PositionAttributeCreateRequest };

export type PositionAttributeTarget = PositionArchitectureTarget & { readonly attributeId: string };

export type UpdatePositionAttributeInput = PositionAttributeTarget & {
    readonly request: PositionAttributeUpdateRequest;
};

export type CreatePositionAttributeValueInput = { readonly request: PositionAttributeValueCreateRequest };

export type UpdatePositionAttributeValueInput = {
    readonly attributeValueId: string;
    readonly request: PositionAttributeValueUpdateRequest;
};

export type PositionAttributeValueTarget = { readonly attributeValueId: string };

export type CreatePositionChartViewInput = PositionArchitectureTarget & { readonly request: PositionChartViewRequest };

export type UpdatePositionChartViewInput = {
    readonly request: PositionChartViewRequest;
    readonly viewId: string;
};

export type PositionChartViewTarget = { readonly viewId: string };

export type UpdatePositionCompanyInformationInput = PositionArchitectureTarget & {
    readonly request: PositionCompanyInformationUpdate;
};

export type CopyPositionCompanyInformationInput = PositionArchitectureTarget & { readonly jobArchitectureId: string };

export type ConnectPositionJobArchitectureInput = PositionArchitectureTarget & { readonly jobArchitectureId: string };

export type SetPositionManagersCanEditInput = PositionArchitectureTarget & { readonly managersCanEdit: boolean };

export type StartPositionCompanyLookupInput = PositionArchitectureTarget & {
    readonly companyDomain: string | null;
    readonly companyName: string;
    readonly regenerate: boolean;
};

export type StartPositionPlanInput = PositionArchitectureTarget & PositionPlanStartRequest;

export type PositionPlanTarget = { readonly planId: string };

export type CreatePositionAttachmentUploadUrlsInput = PositionArchitectureTarget & {
    readonly files: readonly PositionAttachmentPresignFile[];
};

export type LevelMappingRunTarget = PositionArchitectureTarget & { readonly runId: string };

export type StartLevelMappingRunInput = PositionArchitectureTarget & { readonly request: LevelMappingStartRequest };

export type FetchLevelMappingUnitsInput = RequestContext &
    LevelMappingRunTarget & {
        readonly bucket: LevelMappingBucket;
        readonly page: number;
    };

export type ApproveLevelMappingRunInput = LevelMappingRunTarget & {
    readonly changes: readonly LevelMappingUnitChange[];
};

export type CreatePositionAccessGrantInput = PositionArchitectureTarget & {
    readonly request: PositionAccessGrantCreateRequest;
};

export type FetchPositionAccessCoverageInput = RequestContext &
    PositionArchitectureTarget & {
        readonly request: PositionAccessArea;
    };

export type FetchPositionAccessGrantsInput = RequestContext & { readonly userId: string };

export type PositionAccessGrantTarget = { readonly grantId: string };

export type SearchPositionAccessCandidatesInput = RequestContext & {
    readonly limit: number;
    readonly query: string;
};

export type ManagerChangeRunTarget = PositionArchitectureTarget & { readonly runId: string };

export type PreviewManagerChangeInput = RequestContext &
    PositionArchitectureTarget & {
        readonly request: ManagerChangeRequest;
    };

export type StartManagerChangeInput = PositionArchitectureTarget & { readonly request: ManagerChangeStartRequest };

export type FetchNodeManagersInput = RequestContext &
    PositionArchitectureTarget & {
        readonly path: readonly PositionCountsPathEntry[];
    };

export type FetchOrgChartLevelInput = RequestContext &
    PositionArchitectureTarget & {
        readonly request: OrgChartRequest;
    };

export type AddPositionTaskInput = {
    readonly language: string;
    readonly positionId: string;
    readonly title: string;
};

export type EditPositionTaskInput = {
    readonly positionId: string;
    readonly request: PositionTaskEditRequest;
    readonly taskId: string;
};

export type PositionManagementFetchers = {
    readonly approveLevelMappingRun: (input: ApproveLevelMappingRunInput) => Fetched<LevelMappingApprovalResponse>;
    readonly archivePosition: (input: ArchivePositionInput) => Fetched<PositionDetail>;
    readonly cancelLevelMappingRun: (input: LevelMappingRunTarget) => Fetched<unknown>;
    readonly assignPositionReviewer: (input: AssignPositionReviewerInput) => Fetched<PositionReviewer>;
    /** Adds a task the Position owns; its content is generated in the background. */
    readonly addPositionTask: (input: AddPositionTaskInput) => Fetched<PositionTaskRef>;
    readonly connectPositionJobArchitecture: (
        input: ConnectPositionJobArchitectureInput,
    ) => Fetched<PositionArchitecture>;
    readonly copyPositionCompanyInformation: (
        input: CopyPositionCompanyInformationInput,
    ) => Fetched<PositionCompanyInformation>;
    readonly countPositions: (input: CountPositionsInput) => Fetched<PositionCountsResponse>;
    readonly fetchInvitationDeliverySummary: () => Fetched<InvitationDeliverySummary>;
    readonly releaseDeferredInvitations: () => Fetched<ReleaseDeferredInvitations>;
    readonly inviteAndAssignPosition: (
        input: PositionTarget & { readonly request: PositionInvitationRequest; readonly inviteLanguage: string },
    ) => Fetched<PositionDetail>;
    readonly createPosition: (input: CreatePositionInput) => Fetched<PositionDetail>;
    readonly createPositionAccessGrant: (input: CreatePositionAccessGrantInput) => Fetched<PositionAccessGrant>;
    readonly createPositionArchitecture: (input: CreatePositionArchitectureInput) => Fetched<PositionArchitecture>;
    readonly createPositionAttachmentUploadUrls: (
        input: CreatePositionAttachmentUploadUrlsInput,
    ) => Fetched<PositionAttachmentPresignResponse>;
    readonly createPositionAttribute: (input: CreatePositionAttributeInput) => Fetched<PositionAttribute>;
    readonly createPositionAttributeValue: (
        input: CreatePositionAttributeValueInput,
    ) => Fetched<PositionAttributeValue>;
    readonly createPositionChartView: (input: CreatePositionChartViewInput) => Fetched<PositionChartView>;
    readonly deletePosition: (input: PositionTarget) => Fetched<unknown>;
    readonly deletePositionArchitecture: (input: PositionArchitectureTarget) => Fetched<unknown>;
    readonly deletePositionAttribute: (input: PositionAttributeTarget) => Fetched<unknown>;
    readonly deletePositionAttributeValue: (input: PositionAttributeValueTarget) => Fetched<unknown>;
    readonly deletePositionChartView: (input: PositionChartViewTarget) => Fetched<unknown>;
    readonly fetchLatestManagerChange: (
        input: RequestContext & PositionArchitectureTarget,
    ) => Fetched<ManagerChangeRun | null>;
    readonly fetchLevelMappingRun: (input: RequestContext & LevelMappingRunTarget) => Fetched<LevelMappingRun>;
    readonly fetchLevelMappingUnits: (input: FetchLevelMappingUnitsInput) => Fetched<LevelMappingUnitPage>;
    readonly fetchManagerChangeSkipped: (
        input: RequestContext & ManagerChangeRunTarget,
    ) => Fetched<ManagerChangeSkippedPositions>;
    readonly fetchNodeManagers: (input: FetchNodeManagersInput) => Fetched<NodeManagers>;
    readonly fetchOrgChartLevel: (input: FetchOrgChartLevelInput) => Fetched<OrgChartLevel>;
    readonly fetchPosition: (input: FetchPositionInput) => Fetched<PositionDetail>;
    readonly fetchPositionAccessCoverage: (input: FetchPositionAccessCoverageInput) => Fetched<PositionAccessCoverage>;
    readonly fetchPositionAccessGrants: (input: FetchPositionAccessGrantsInput) => Fetched<PositionAccessGrantList>;
    readonly fetchPositionAttachments: (
        input: RequestContext & PositionArchitectureTarget,
    ) => Fetched<PositionAttachmentList>;
    readonly fetchPositionBootstrap: (input: RequestContext) => Fetched<PositionBootstrap>;
    readonly fetchPositionCompanyInformation: (
        input: RequestContext & PositionArchitectureTarget,
    ) => Fetched<PositionCompanyInformation>;
    readonly fetchPositionCompanyLookup: (
        input: RequestContext & PositionArchitectureTarget,
    ) => Fetched<PositionCompanyLookup>;
    readonly fetchPositionEvents: (input: FetchPositionEventsInput) => Fetched<PositionEventPage>;
    readonly fetchPositionLevelPickerChildren: (
        input: FetchPositionLevelPickerChildrenInput,
    ) => Fetched<PositionLevelPickerChildren>;
    readonly fetchPositionLevelCounts: (input: RequestContext) => Fetched<PositionLevelCounts>;
    readonly fetchPositionPlan: (input: RequestContext & PositionPlanTarget) => Fetched<PositionPlan>;
    readonly fetchPositionPlans: (
        input: RequestContext & PositionArchitectureTarget,
    ) => Fetched<readonly PositionPlanListItem[]>;
    readonly fetchPendingHolderInvitations: (
        input: RequestContext & PositionArchitectureTarget & { readonly selection: HolderInvitationSelection },
    ) => Fetched<HolderInvitationsPending>;
    readonly fetchPositionWorkspace: (input: RequestContext & PositionArchitectureTarget) => Fetched<PositionWorkspace>;
    readonly filterPositions: (input: FilterPositionsInput) => Fetched<PositionPage>;
    readonly fetchPositionWorkQueueCounts: (
        input: RequestContext & PositionArchitectureTarget,
    ) => Fetched<PositionWorkQueueCounts>;
    readonly mapPositionLevel: (input: MapPositionLevelInput) => Fetched<PositionDetail>;
    readonly previewManagerChange: (input: PreviewManagerChangeInput) => Fetched<ManagerChangePreview>;
    readonly publishPositionVersion: (input: PositionVersionTarget) => Fetched<unknown>;
    readonly removePositionReviewer: (input: PositionTarget) => Fetched<unknown>;
    readonly retryManagerChange: (input: ManagerChangeRunTarget) => Fetched<ManagerChangeRun>;
    readonly retryPositionPlan: (input: PositionPlanTarget) => Fetched<PositionPlan>;
    readonly retryPositionTranslation: (input: PositionVersionTarget) => Fetched<unknown>;
    readonly revokePositionAccessGrant: (input: PositionAccessGrantTarget) => Fetched<unknown>;
    readonly reviewPositionVersion: (input: PositionVersionTarget) => Fetched<unknown>;
    readonly savePosition: (input: SavePositionInput) => Fetched<PositionDetail>;
    /** Edits a task from the Position: forks a shared task on its first edit, versions the Position's own fork after. */
    readonly editPositionTask: (input: EditPositionTaskInput) => Fetched<PositionTaskRef>;
    readonly setDefaultPositionArchitecture: (input: PositionArchitectureTarget) => Fetched<PositionArchitecture>;
    readonly setPositionManagersCanEdit: (input: SetPositionManagersCanEditInput) => Fetched<PositionArchitecture>;
    readonly searchPositionAccessCandidates: (
        input: SearchPositionAccessCandidatesInput,
    ) => Fetched<PositionAccessCandidates>;
    readonly searchPositionAssignees: (input: SearchPositionAssigneesInput) => Fetched<readonly PositionPerson[]>;
    readonly searchPositionLevelPickerLevels: (
        input: SearchPositionLevelPickerLevelsInput,
    ) => Fetched<readonly PositionLevelPickerLevel[]>;
    readonly searchPositionReviewers: (input: SearchPositionReviewersInput) => Fetched<readonly PositionPerson[]>;
    readonly sendHolderInvitations: (
        input: PositionArchitectureTarget & { readonly selection: HolderInvitationSelection },
    ) => Fetched<HolderInvitationsSent>;
    readonly startManagerChange: (input: StartManagerChangeInput) => Fetched<ManagerChangeRun>;
    readonly startPositionCompanyLookup: (input: StartPositionCompanyLookupInput) => Fetched<PositionCompanyLookup>;
    readonly startPositionPlan: (input: StartPositionPlanInput) => Fetched<PositionPlan>;
    readonly startLevelMappingRun: (input: StartLevelMappingRunInput) => Fetched<LevelMappingRun>;
    readonly unpublishPositionVersion: (input: PositionVersionTarget) => Fetched<unknown>;
    readonly unreviewPositionVersion: (input: PositionVersionTarget) => Fetched<unknown>;
    readonly updatePositionAttribute: (input: UpdatePositionAttributeInput) => Fetched<PositionAttribute>;
    readonly updatePositionAttributeValue: (
        input: UpdatePositionAttributeValueInput,
    ) => Fetched<PositionAttributeValue>;
    readonly updatePositionChartView: (input: UpdatePositionChartViewInput) => Fetched<PositionChartView>;
    readonly updatePositionCompanyInformation: (
        input: UpdatePositionCompanyInformationInput,
    ) => Fetched<PositionCompanyInformation>;
    readonly updatePositionOperations: (input: UpdatePositionOperationsInput) => Fetched<PositionDetail>;
};

export type PositionManagementFetchersDependencies = {
    readonly cobrainerApiFetcher: HttpFetcherWithBaseUrl;
    readonly validatingCobrainerApiFetcher: ValidatingHttpFetcherWithBaseUrl;
};

const levelMappingRunsPath = (architectureId: string): `/${string}` =>
    `/position-management/architectures/${architectureId}/level-mapping-runs`;

const accessGrantsPath = (architectureId: string): `/${string}` =>
    `/position-management/architectures/${architectureId}/access-grants`;

const managerChangesPath = (architectureId: string): `/${string}` =>
    `/position-management/architectures/${architectureId}/manager-changes`;

const holderInvitationSelectionPayload = (selection: HolderInvitationSelection) => ({
    ...(selection.kind === 'all' ? { all: true } : { position_ids: selection.positionIds }),
    ...(selection.audience === undefined ? {} : { audience: selection.audience }),
});

export const createPositionManagementFetchers = ({
    cobrainerApiFetcher,
    validatingCobrainerApiFetcher,
}: PositionManagementFetchersDependencies): PositionManagementFetchers => ({
    fetchInvitationDeliverySummary: () =>
        validatingCobrainerApiFetcher.get(invitationDeliverySummarySchema, {
            pathname: '/user-management/invitations/delivery-summary',
        }),
    releaseDeferredInvitations: () =>
        validatingCobrainerApiFetcher.post(releaseDeferredInvitationsSchema, {
            pathname: '/user-management/invitations/release-deferred',
        }),
    inviteAndAssignPosition: ({ positionId, request, inviteLanguage }) =>
        validatingCobrainerApiFetcher.postJson(positionDetailSchema, {
            pathname: `/position-management/positions/${positionId}/invite-and-assign`,
            payload: request,
            requestOptions: { searchParams: new URLSearchParams({ inviteLanguage }) },
        }),
    approveLevelMappingRun: ({ architectureId, changes, runId }) =>
        validatingCobrainerApiFetcher.postJson(levelMappingApprovalResponseSchema, {
            pathname: `${levelMappingRunsPath(architectureId)}/${runId}/approval`,
            payload: { changes },
        }),
    archivePosition: ({ archived, positionId }) =>
        validatingCobrainerApiFetcher.postJson(positionDetailSchema, {
            pathname: `/position-management/positions/${positionId}/archive`,
            payload: { archived },
        }),
    assignPositionReviewer: ({ positionId, userId }) =>
        validatingCobrainerApiFetcher.postJson(positionReviewerSchema, {
            pathname: `/position-management/positions/${positionId}/reviewer`,
            payload: { user_id: userId },
        }),
    addPositionTask: ({ language, positionId, title }) =>
        validatingCobrainerApiFetcher.postJson(positionTaskRefSchema, {
            pathname: `/position-management/positions/${positionId}/tasks`,
            payload: { language, title },
        }),
    connectPositionJobArchitecture: ({ architectureId, jobArchitectureId }) =>
        validatingCobrainerApiFetcher.putJson(positionArchitectureSchema, {
            pathname: `/position-management/architectures/${architectureId}/connected-job-architecture`,
            payload: { job_architecture_id: jobArchitectureId },
        }),
    copyPositionCompanyInformation: ({ architectureId, jobArchitectureId }) =>
        validatingCobrainerApiFetcher.postJson(positionCompanyInformationSchema, {
            pathname: `/position-management/architectures/${architectureId}/company-information/copy-from-job-architecture`,
            payload: { job_architecture_id: jobArchitectureId },
        }),
    cancelLevelMappingRun: ({ architectureId, runId }) =>
        cobrainerApiFetcher.delete({ pathname: `${levelMappingRunsPath(architectureId)}/${runId}` }),
    countPositions: ({ architectureId, request, signal }) =>
        validatingCobrainerApiFetcher.postJson(positionCountsResponseSchema, {
            pathname: `/position-management/architectures/${architectureId}/positions/counts`,
            payload: request,
            requestOptions: { signal },
        }),
    createPosition: ({ request }) =>
        validatingCobrainerApiFetcher.postJson(positionDetailSchema, {
            pathname: '/position-management/positions',
            payload: request,
        }),
    createPositionAccessGrant: ({ architectureId, request }) =>
        validatingCobrainerApiFetcher.postJson(positionAccessGrantSchema, {
            expectedStatusCodes: [409],
            pathname: accessGrantsPath(architectureId),
            payload: request,
        }),
    createPositionArchitecture: ({ jobArchitectureId, name }) =>
        validatingCobrainerApiFetcher.postJson(positionArchitectureSchema, {
            pathname: '/position-management/architectures',
            payload: jobArchitectureId === undefined ? { name } : { job_architecture_id: jobArchitectureId, name },
        }),
    createPositionAttachmentUploadUrls: ({ architectureId, files }) =>
        validatingCobrainerApiFetcher.postJson(positionAttachmentPresignResponseSchema, {
            pathname: `/position-management/architectures/${architectureId}/attachments/presigned-urls`,
            payload: { files },
        }),
    createPositionAttribute: ({ request }) =>
        validatingCobrainerApiFetcher.postJson(positionAttributeSchema, {
            pathname: '/position-management/attributes',
            payload: request,
        }),
    createPositionAttributeValue: ({ request }) =>
        validatingCobrainerApiFetcher.postJson(positionAttributeValueSchema, {
            pathname: '/position-management/attribute-values',
            payload: request,
        }),
    createPositionChartView: ({ architectureId, request }) =>
        validatingCobrainerApiFetcher.postJson(positionChartViewSchema, {
            pathname: '/position-management/views',
            payload: { architecture_id: architectureId, ...request },
        }),
    deletePosition: ({ positionId }) =>
        cobrainerApiFetcher.delete({ pathname: `/position-management/positions/${positionId}` }),
    deletePositionArchitecture: ({ architectureId }) =>
        cobrainerApiFetcher.delete({ pathname: `/position-management/architectures/${architectureId}` }),
    deletePositionAttribute: ({ architectureId, attributeId }) =>
        cobrainerApiFetcher.delete({
            pathname: `/position-management/attributes/${attributeId}`,
            requestOptions: { searchParams: { architecture_id: architectureId } },
        }),
    deletePositionAttributeValue: ({ attributeValueId }) =>
        cobrainerApiFetcher.delete({ pathname: `/position-management/attribute-values/${attributeValueId}` }),
    deletePositionChartView: ({ viewId }) =>
        cobrainerApiFetcher.delete({ pathname: `/position-management/views/${viewId}` }),
    fetchLatestManagerChange: ({ architectureId, signal }) =>
        validatingCobrainerApiFetcher.get(latestManagerChangeSchema, {
            pathname: `${managerChangesPath(architectureId)}/latest`,
            requestOptions: { signal },
        }),
    fetchLevelMappingRun: ({ architectureId, runId, signal }) =>
        validatingCobrainerApiFetcher.get(levelMappingRunSchema, {
            pathname: `${levelMappingRunsPath(architectureId)}/${runId}`,
            requestOptions: { signal },
        }),
    fetchLevelMappingUnits: ({ architectureId, bucket, page, runId, signal }) =>
        validatingCobrainerApiFetcher.get(levelMappingUnitPageSchema, {
            pathname: `${levelMappingRunsPath(architectureId)}/${runId}/units`,
            requestOptions: { searchParams: { bucket, page }, signal },
        }),
    fetchManagerChangeSkipped: ({ architectureId, runId, signal }) =>
        validatingCobrainerApiFetcher.get(managerChangeSkippedPositionsSchema, {
            pathname: `${managerChangesPath(architectureId)}/${runId}/skipped`,
            requestOptions: { signal },
        }),
    fetchNodeManagers: ({ architectureId, path, signal }) =>
        validatingCobrainerApiFetcher.postJson(nodeManagersSchema, {
            pathname: `/position-management/architectures/${architectureId}/node-managers`,
            payload: { path },
            requestOptions: { signal },
        }),
    fetchOrgChartLevel: ({ architectureId, request, signal }) =>
        validatingCobrainerApiFetcher.postJson(orgChartLevelSchema, {
            pathname: `/position-management/architectures/${architectureId}/org-chart/children`,
            payload: request,
            requestOptions: { signal },
        }),
    fetchPosition: ({ positionId, signal, version }) =>
        validatingCobrainerApiFetcher.get(positionDetailSchema, {
            pathname: `/position-management/positions/${positionId}`,
            requestOptions: { searchParams: { version }, signal },
        }),
    fetchPositionAccessCoverage: ({ architectureId, request, signal }) =>
        validatingCobrainerApiFetcher.postJson(positionAccessCoverageSchema, {
            pathname: `${accessGrantsPath(architectureId)}/coverage`,
            payload: request,
            requestOptions: { signal },
        }),
    fetchPositionAccessGrants: ({ signal, userId }) =>
        validatingCobrainerApiFetcher.get(positionAccessGrantListSchema, {
            pathname: '/position-management/access-grants',
            requestOptions: { searchParams: { user_id: userId }, signal },
        }),
    fetchPositionAttachments: ({ architectureId, signal }) =>
        validatingCobrainerApiFetcher.get(positionAttachmentListSchema, {
            pathname: `/position-management/architectures/${architectureId}/attachments`,
            requestOptions: { signal },
        }),
    fetchPositionBootstrap: ({ signal }) =>
        validatingCobrainerApiFetcher.get(positionBootstrapSchema, {
            expectedStatusCodes: [403],
            pathname: '/position-management/bootstrap',
            requestOptions: { signal },
        }),
    fetchPositionCompanyInformation: ({ architectureId, signal }) =>
        validatingCobrainerApiFetcher.get(positionCompanyInformationSchema, {
            pathname: `/position-management/architectures/${architectureId}/company-information`,
            requestOptions: { signal },
        }),
    fetchPositionCompanyLookup: ({ architectureId, signal }) =>
        validatingCobrainerApiFetcher.get(positionCompanyLookupSchema, {
            pathname: `/position-management/architectures/${architectureId}/company-information/generate`,
            requestOptions: { signal },
        }),
    fetchPositionEvents: ({ page, positionId, signal, size }) =>
        validatingCobrainerApiFetcher.get(positionEventPageSchema, {
            pathname: `/position-management/positions/${positionId}/events`,
            requestOptions: { searchParams: { page, size }, signal },
        }),
    fetchPositionLevelPickerChildren: ({ jobArchitectureId, parentId, signal }) =>
        validatingCobrainerApiFetcher.get(positionLevelPickerChildrenSchema, {
            pathname: `/position-management/level-picker/job-architectures/${jobArchitectureId}/children`,
            requestOptions: { searchParams: { parent_id: parentId ?? undefined }, signal },
        }),
    fetchPositionLevelCounts: ({ signal }) =>
        validatingCobrainerApiFetcher.get(positionLevelCountsSchema, {
            pathname: '/position-management/level-position-counts',
            requestOptions: { signal },
        }),
    fetchPositionPlan: ({ planId, signal }) =>
        validatingCobrainerApiFetcher.get(positionPlanSchema, {
            pathname: `/position-management/plans/${planId}`,
            requestOptions: { signal },
        }),
    fetchPositionPlans: ({ architectureId, signal }) =>
        validatingCobrainerApiFetcher.get(positionPlanListSchema, {
            pathname: `/position-management/architectures/${architectureId}/plans`,
            requestOptions: { signal },
        }),
    fetchPendingHolderInvitations: ({ architectureId, selection, signal }) =>
        validatingCobrainerApiFetcher.postJson(holderInvitationsPendingSchema, {
            pathname: `/position-management/architectures/${architectureId}/holder-invitations/pending`,
            payload: holderInvitationSelectionPayload(selection),
            requestOptions: { signal },
        }),
    fetchPositionWorkspace: ({ architectureId, signal }) =>
        validatingCobrainerApiFetcher.get(positionWorkspaceSchema, {
            pathname: `/position-management/architectures/${architectureId}/workspace`,
            requestOptions: { signal },
        }),
    filterPositions: ({ architectureId, request, signal }) =>
        validatingCobrainerApiFetcher.postJson(positionPageSchema, {
            pathname: `/position-management/architectures/${architectureId}/positions/filter`,
            payload: request,
            requestOptions: { signal },
        }),
    fetchPositionWorkQueueCounts: ({ architectureId, signal }) =>
        validatingCobrainerApiFetcher.get(positionWorkQueueCountsSchema, {
            pathname: `/position-management/architectures/${architectureId}/positions/work-queues`,
            requestOptions: { signal },
        }),
    mapPositionLevel: ({ mappedLevelItemId, positionId }) =>
        validatingCobrainerApiFetcher.postJson(positionDetailSchema, {
            pathname: `/position-management/positions/${positionId}/map-level`,
            payload: { mapped_level_item_id: mappedLevelItemId },
        }),
    previewManagerChange: ({ architectureId, request, signal }) =>
        validatingCobrainerApiFetcher.postJson(managerChangePreviewSchema, {
            pathname: `${managerChangesPath(architectureId)}/preview`,
            payload: request,
            requestOptions: { signal },
        }),
    publishPositionVersion: ({ positionId, version }) =>
        cobrainerApiFetcher.post({
            pathname: `/position-management/positions/${positionId}/versions/${version}/publish`,
        }),
    removePositionReviewer: ({ positionId }) =>
        cobrainerApiFetcher.delete({ pathname: `/position-management/positions/${positionId}/reviewer` }),
    editPositionTask: ({ positionId, request, taskId }) =>
        validatingCobrainerApiFetcher.putJson(positionTaskRefSchema, {
            pathname: `/position-management/positions/${positionId}/tasks/${taskId}`,
            payload: request,
        }),
    retryManagerChange: ({ architectureId, runId }) =>
        validatingCobrainerApiFetcher.post(managerChangeRunSchema, {
            expectedStatusCodes: [409],
            pathname: `${managerChangesPath(architectureId)}/${runId}/retry`,
        }),
    retryPositionPlan: ({ planId }) =>
        validatingCobrainerApiFetcher.post(positionPlanSchema, {
            pathname: `/position-management/plans/${planId}/retry`,
        }),
    retryPositionTranslation: ({ positionId, version }) =>
        cobrainerApiFetcher.post({
            pathname: `/position-management/positions/${positionId}/versions/${version}/translations/retry`,
        }),
    revokePositionAccessGrant: ({ grantId }) =>
        cobrainerApiFetcher.delete({ pathname: `/position-management/access-grants/${grantId}` }),
    reviewPositionVersion: ({ positionId, version }) =>
        cobrainerApiFetcher.post({
            pathname: `/position-management/positions/${positionId}/versions/${version}/review`,
        }),
    savePosition: ({ positionId, request }) =>
        validatingCobrainerApiFetcher.postJson(positionDetailSchema, {
            pathname: `/position-management/positions/${positionId}/save`,
            payload: request,
        }),
    setDefaultPositionArchitecture: ({ architectureId }) =>
        validatingCobrainerApiFetcher.put(positionArchitectureSchema, {
            pathname: `/position-management/architectures/${architectureId}/default`,
        }),
    setPositionManagersCanEdit: ({ architectureId, managersCanEdit }) =>
        validatingCobrainerApiFetcher.putJson(positionArchitectureSchema, {
            pathname: `/position-management/architectures/${architectureId}/managers-can-edit`,
            payload: { managers_can_edit: managersCanEdit },
        }),
    searchPositionAccessCandidates: ({ limit, query, signal }) =>
        validatingCobrainerApiFetcher.get(positionAccessCandidatesSchema, {
            pathname: '/position-management/access-grants/candidates',
            requestOptions: { searchParams: { limit, search_term: query }, signal },
        }),
    searchPositionAssignees: ({ limit, query, signal }) =>
        validatingCobrainerApiFetcher.get(positionAssigneeCandidatesSchema, {
            pathname: '/position-management/assignees',
            requestOptions: { searchParams: { limit, query }, signal },
        }),
    searchPositionLevelPickerLevels: ({ jobArchitectureId, languageTag, limit, query, signal }) =>
        validatingCobrainerApiFetcher.get(positionLevelPickerLevelsSchema, {
            pathname: `/position-management/level-picker/job-architectures/${jobArchitectureId}/levels`,
            requestOptions: { headers: { 'Accept-Language': languageTag }, searchParams: { limit, query }, signal },
        }),
    searchPositionReviewers: ({ query, signal }) =>
        validatingCobrainerApiFetcher.get(positionReviewerCandidatesSchema, {
            pathname: '/position-management/reviewers',
            requestOptions: { searchParams: { query }, signal },
        }),
    sendHolderInvitations: ({ architectureId, selection }) =>
        validatingCobrainerApiFetcher.postJson(holderInvitationsSentSchema, {
            pathname: `/position-management/architectures/${architectureId}/holder-invitations/send`,
            payload: holderInvitationSelectionPayload(selection),
        }),
    startManagerChange: ({ architectureId, request }) =>
        validatingCobrainerApiFetcher.postJson(managerChangeRunSchema, {
            expectedStatusCodes: [409],
            pathname: managerChangesPath(architectureId),
            payload: request,
        }),
    startPositionCompanyLookup: ({ architectureId, companyDomain, companyName, regenerate }) =>
        validatingCobrainerApiFetcher.postJson(positionCompanyLookupSchema, {
            pathname: `/position-management/architectures/${architectureId}/company-information/generate`,
            payload: { company_domain: companyDomain, company_name: companyName },
            requestOptions: { searchParams: { regenerate } },
        }),
    startPositionPlan: ({ architectureId, request, setupRunId }) =>
        validatingCobrainerApiFetcher.postJson(positionPlanSchema, {
            expectedStatusCodes: [409],
            pathname: `/position-management/architectures/${architectureId}/plans`,
            payload: { request, setupRunId },
        }),
    startLevelMappingRun: ({ architectureId, request }) =>
        validatingCobrainerApiFetcher.postJson(levelMappingRunSchema, {
            expectedStatusCodes: [409],
            pathname: levelMappingRunsPath(architectureId),
            payload: request,
        }),
    unpublishPositionVersion: ({ positionId, version }) =>
        cobrainerApiFetcher.post({
            pathname: `/position-management/positions/${positionId}/versions/${version}/unpublish`,
        }),
    unreviewPositionVersion: ({ positionId, version }) =>
        cobrainerApiFetcher.post({
            pathname: `/position-management/positions/${positionId}/versions/${version}/unreview`,
        }),
    updatePositionAttribute: ({ architectureId, attributeId, request }) =>
        validatingCobrainerApiFetcher.putJson(positionAttributeSchema, {
            pathname: `/position-management/attributes/${attributeId}`,
            payload: request,
            requestOptions: { searchParams: { architecture_id: architectureId } },
        }),
    updatePositionAttributeValue: ({ attributeValueId, request }) =>
        validatingCobrainerApiFetcher.putJson(positionAttributeValueSchema, {
            pathname: `/position-management/attribute-values/${attributeValueId}`,
            payload: request,
        }),
    updatePositionChartView: ({ request, viewId }) =>
        validatingCobrainerApiFetcher.putJson(positionChartViewSchema, {
            pathname: `/position-management/views/${viewId}`,
            payload: request,
        }),
    updatePositionCompanyInformation: ({ architectureId, request }) =>
        validatingCobrainerApiFetcher.putJson(positionCompanyInformationSchema, {
            pathname: `/position-management/architectures/${architectureId}/company-information`,
            payload: request,
        }),
    updatePositionOperations: ({ operations, positionId }) =>
        validatingCobrainerApiFetcher.postJson(positionDetailSchema, {
            pathname: `/position-management/positions/${positionId}/operations`,
            payload: operations,
        }),
});
